import React, { useState } from 'react';
import { getRoomColor } from '../utils/roomColors';
import { pairRooms } from '../utils/roomOrdering';

// Stored upper-case to match how the spreadsheets display and group floors.
const FLOOR_OPTIONS = ['BASEMENT', '1ST FLOOR', '2ND FLOOR', '3RD FLOOR', '4TH FLOOR', '5TH FLOOR', 'ATTIC'];

const AddMultipleRoomsModal = ({ onClose, onSubmit, roomColors, existingRooms = [], floorOptions }) => {
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [customRoom, setCustomRoom] = useState('');
  const [loading, setLoading] = useState(false);
  const [defaultFloor, setDefaultFloor] = useState('1ST FLOOR');
  const [roomFloors, setRoomFloors] = useState({});   // { roomName: floor } — per-room override
  const existingCount = existingRooms.length;

  // Offer any floors the project already uses, plus the standard set.
  const floors = Array.from(new Set([
    ...(floorOptions || []),
    ...existingRooms.map(r => r.floor).filter(Boolean),
    ...FLOOR_OPTIONS,
  ]));

  const floorFor = (roomName) => roomFloors[roomName] || defaultFloor;

  // Room list ordered by INTERIOR DESIGN PROJECT PRIORITY
  // First 10: Main living spaces designers typically start with
  const commonRooms = [
    // PRIMARY LIVING SPACES (First priority - these are worked on first)
    'Living Room',
    'Kitchen', 
    'Primary Bedroom',
    'Primary Bathroom',
    'Dining Room',
    'Family Room',
    'Guest Bedroom',
    'Guest Bathroom',
    'Home Office',
    'Laundry Room',
    
    // SECONDARY LIVING SPACES
    'Great Room',
    'Breakfast Nook',
    'Powder Room',
    'Mudroom',
    'Foyer',
    'Pantry',
    'Butler\'s Pantry',
    
    // ADDITIONAL BEDROOMS & BATHROOMS
    // Each bedroom is followed by the bathroom that goes with it.
    'Bedroom 2',
    'Bathroom 2',
    'Bedroom 3',
    'Bathroom 3',
    'Bedroom 4',
    'Bathroom 4',
    'Guest Bedroom 2',
    'Guest Bathroom 2',
    'Guest Bedroom 3',
    'Guest Bathroom 3',
    'Children\'s Bedroom',
    'Nursery',
    'Jack and Jill Bathroom',
    
    // CLOSETS & STORAGE
    'Primary Closet',
    'Walk-in Closet',
    'Linen Closet',
    'Storage Room',
    
    // SPECIALTY ROOMS
    'Den',
    'Study',
    'Library',
    'Media Room',
    'Home Theater',
    'Game Room',
    'Music Room',
    'Craft Room',
    'Art Studio',
    'Home Gym',
    'Wine Cellar',
    'Bar Area',
    'Bonus Room',
    'Play Room',
    
    // UTILITY & SERVICE
    'Scullery',
    'Utility Room',
    'Workshop',
    'Garage',
    
    // TRANSITIONAL SPACES
    'Hallway',
    'Stairway',
    'Landing',
    'Sunroom',
    'Screened Porch',
    
    // OUTDOOR SPACES (Last - typically completed after interior)
    'Patio',
    'Deck',
    'Covered Porch',
    'Front Porch',
    'Back Porch',
    'Balcony',
    'Terrace',
    'Outdoor Kitchen',
    'Pool House',
    'Cabana',
    'Pool Bathroom',
    
    // SEPARATE STRUCTURES
    'Guest House',
    'Mother-in-Law Suite',
    'She Shed',
    'Man Cave'
  ];

  // Filter out rooms that already exist in the project
  const existingRoomNames = existingRooms.map(r => r.name?.toLowerCase());
  const availableRooms = pairRooms(commonRooms.filter(room => 
    !existingRoomNames.includes(room.toLowerCase())
  ));

  // What you see in the preview is the order the rooms get created in, so the
  // matching bathroom lands beside its bedroom on every sheet.
  const orderedSelection = pairRooms(selectedRooms);

  const handleToggleRoom = (roomName) => {
    setSelectedRooms(prev => {
      if (prev.includes(roomName)) {
        return prev.filter(r => r !== roomName);
      } else {
        return [...prev, roomName];
      }
    });
  };

  const handleAddCustomRoom = () => {
    if (customRoom.trim() && !selectedRooms.includes(customRoom.trim())) {
      setSelectedRooms(prev => [...prev, customRoom.trim()]);
      setCustomRoom('');
    }
  };

  const handleRemoveSelected = (roomName) => {
    setSelectedRooms(prev => prev.filter(r => r !== roomName));
  };

  const handleSelectAll = () => {
    setSelectedRooms(availableRooms);
  };

  const handleClearAll = () => {
    setSelectedRooms([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedRooms.length === 0) return;

    setLoading(true);
    try {
      // Submit all rooms, each with the floor chosen for it
      for (const roomName of orderedSelection) {
        await onSubmit({ name: roomName, description: '', floor: floorFor(roomName) });
      }
      onClose();
    } catch (err) {
      console.error('Error submitting rooms:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
      <div className="rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto mt-4 border-2 border-[#D4A574]" style={{
        background: 'linear-gradient(135deg, rgba(0,0,0,0.98) 0%, rgba(30,30,30,0.95) 20%, rgba(15,15,25,0.98) 40%, rgba(30,30,30,0.95) 60%, rgba(15,15,25,0.98) 80%, rgba(0,0,0,0.98) 100%)',
        boxShadow: '0 0 60px rgba(212, 165, 116, 0.3), inset 0 0 80px rgba(212, 165, 116, 0.05)'
      }}>
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="p-6 border-b-2 border-[#D4A574]">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-[#D4A574]">Add Multiple Rooms</h2>
              <button
                type="button"
                onClick={onClose}
                className="text-[#D4A574] hover:text-red-400 transition-colors text-2xl"
              >
                ✕
              </button>
            </div>
            <p className="text-[#D4C5A9] text-sm mt-2">
              Select multiple rooms to add at once. Click on rooms to select/deselect.
            </p>
          </div>

          {/* Selected Rooms Preview */}
          {selectedRooms.length > 0 && (
            <div className="px-6 pt-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-[#D4A574]">
                  Selected Rooms ({selectedRooms.length})
                </label>
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="text-xs text-red-400 hover:text-red-300"
                >
                  Clear All
                </button>
              </div>
              {/* Floor for the whole batch — each room can still be changed below */}
              <div className="flex items-center gap-2 mb-3">
                <label className="text-xs font-medium text-[#D4C5A9]">Put these on</label>
                <select
                  value={defaultFloor}
                  onChange={(e) => setDefaultFloor(e.target.value)}
                  data-testid="batch-floor-select"
                  className="px-3 py-1.5 rounded-lg border-2 border-[#B49B7E] focus:border-[#D4A574] focus:outline-none text-[#D4C5A9] text-sm"
                  style={{ background: 'rgba(0,0,0,0.9)' }}
                >
                  {floors.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
                <span className="text-[11px] text-[#B49B7E]">— change any individual room on the right</span>
              </div>
              <div className="space-y-1 p-3 rounded-lg border border-[#D4A574]/30 max-h-56 overflow-y-auto" style={{
                background: 'rgba(212, 165, 116, 0.05)'
              }}>
                {orderedSelection.map((room, index) => {
                  const roomColor = getRoomColor(room, existingCount + index);
                  return (
                    <div key={room} className="flex items-center justify-between gap-2">
                      <span
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium text-white cursor-pointer hover:opacity-80"
                        style={{ backgroundColor: roomColor }}
                        onClick={() => handleRemoveSelected(room)}
                      >
                        {room}
                        <span className="ml-1 text-white/80">×</span>
                      </span>
                      <select
                        value={floorFor(room)}
                        onChange={(e) => setRoomFloors(prev => ({ ...prev, [room]: e.target.value }))}
                        data-testid={`floor-select-${room}`}
                        className="px-2 py-1 rounded border border-[#B49B7E]/60 text-[#D4C5A9] text-xs"
                        style={{ background: 'rgba(0,0,0,0.9)' }}
                      >
                        {floors.map(f => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Custom Room Input */}
            <div>
              <label className="block text-sm font-medium text-[#D4C5A9] mb-2">
                Add Custom Room
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={customRoom}
                  onChange={(e) => setCustomRoom(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCustomRoom())}
                  className="flex-1 px-4 py-3 rounded-lg border-2 border-[#B49B7E] focus:border-[#D4A574] focus:outline-none text-[#D4C5A9]"
                  style={{
                    background: 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(20,20,30,0.9) 50%, rgba(0,0,0,0.95) 100%)'
                  }}
                  placeholder="Enter custom room name..."
                />
                <button
                  type="button"
                  onClick={handleAddCustomRoom}
                  className="px-4 py-2 rounded-lg border-2 border-[#D4A574] text-[#D4A574] hover:bg-[#D4A574] hover:text-black transition-colors font-medium"
                  disabled={!customRoom.trim()}
                >
                  Add
                </button>
              </div>
            </div>

            {/* Quick Room Selection */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-[#D4C5A9]">
                  Quick Select Common Rooms
                </label>
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="text-xs text-[#D4A574] hover:text-[#D4C5A9]"
                >
                  Select All Available
                </button>
              </div>
              
              {availableRooms.length === 0 ? (
                <p className="text-[#D4C5A9]/60 text-sm italic">
                  All common rooms have been added to this project.
                </p>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  {availableRooms.map((room, index) => {
                    const roomColor = getRoomColor(room, existingCount + index);
                    const isSelected = selectedRooms.includes(room);
                    return (
                      <button
                        key={room}
                        type="button"
                        onClick={() => handleToggleRoom(room)}
                        className={`p-3 rounded-xl border-2 font-bold text-sm transition-all overflow-hidden relative ${
                          isSelected 
                            ? 'border-[#D4A574] text-[#D4A574]' 
                            : 'border-[#B49B7E] hover:border-[#D4A574] text-[#D4C5A9]'
                        }`}
                        style={{ 
                          background: isSelected 
                            ? `linear-gradient(135deg, rgba(212,165,116,0.15) 0%, rgba(30,30,30,0.9) 50%, rgba(212,165,116,0.15) 100%)`
                            : 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(30,30,30,0.9) 30%, rgba(15,15,25,0.95) 70%, rgba(0,0,0,0.95) 100%)',
                          borderTop: `4px solid ${roomColor}`,
                          boxShadow: isSelected 
                            ? `0 0 20px ${roomColor}80, 0 -3px 12px ${roomColor}50, inset 0 0 25px ${roomColor}15` 
                            : `0 -2px 6px ${roomColor}30, inset 0 0 15px ${roomColor}04`
                        }}
                      >
                        {isSelected && (
                          <span className="absolute top-1 right-1 text-[#D4A574] text-lg">✓</span>
                        )}
                        {room}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t-2 border-[#D4A574] flex justify-between items-center">
            <span className="text-[#D4C5A9] text-sm">
              {selectedRooms.length} room{selectedRooms.length !== 1 ? 's' : ''} selected
            </span>
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 text-[#D4C5A9] hover:text-[#D4A574] transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 rounded-lg transition-colors font-medium disabled:opacity-50 border-2 border-[#D4A574] text-[#D4A574] hover:bg-[#D4A574] hover:text-black"
                style={{
                  background: loading || selectedRooms.length === 0 ? 'rgba(212, 165, 116, 0.1)' : 'linear-gradient(135deg, rgba(0,0,0,0.9) 0%, rgba(30,30,30,0.85) 50%, rgba(0,0,0,0.9) 100%)'
                }}
                disabled={loading || selectedRooms.length === 0}
              >
                {loading ? 'Creating...' : `Create ${selectedRooms.length} Room${selectedRooms.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddMultipleRoomsModal;
